import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { CalendarDaysIcon, Clock } from 'lucide-react'
import { useParams } from 'react-router'

import { Card, CardContent } from '@/components/elements/card'
import { Skeleton } from '@/components/elements/skeleton'
import { TextLink } from '@/components/TextLink'
import { getEvents } from '@/services/events.service'
import { formatTimeRange } from '@/utils/formatters'

function UpcomingEventsSection() {
  const { entityId } = useParams()

  const filters = {
    entity: entityId,
    date: { after: dayjs().format('YYYY-MM-DD') },
  }

  const { data: events, isPending } = useQuery({
    queryKey: ['events', 'dashboard-upcoming', filters],
    queryFn: () => getEvents(filters),
    enabled: !!entityId,
  })

  const upcomingEvents = events?.results?.slice(0, 5) ?? []

  if (isPending) {
    return (
      <section>
        <h2 className="mb-4 flex items-center gap-2 uppercase">
          <CalendarDaysIcon className="h-5 w-5" />
          Upcoming Events
        </h2>
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </CardContent>
        </Card>
      </section>
    )
  }

  if (upcomingEvents.length === 0) {
    return (
      <section>
        <h2 className="mb-4 flex items-center gap-2 uppercase">
          <CalendarDaysIcon className="h-5 w-5" />
          Upcoming Events
        </h2>
        <Card>
          <CardContent className="py-8 text-center">
            <CalendarDaysIcon className="text-muted-foreground/50 mx-auto mb-3 h-10 w-10" />
            <h3 className="mb-1 text-base font-semibold">No Upcoming Events</h3>
            <p className="text-muted-foreground text-sm">
              Scheduled training sessions and events will appear here.
            </p>
          </CardContent>
        </Card>
      </section>
    )
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 uppercase">
          <CalendarDaysIcon className="h-5 w-5" />
          Upcoming Events
        </h2>
        <TextLink to={`/accounts/${entityId}/calendar`}>View Calendar</TextLink>
      </div>
      <Card>
        <CardContent className="py-4">
          <ul className="space-y-3">
            {upcomingEvents.map((event) => (
              <li
                key={event.id}
                className="flex items-center justify-between border-b pb-3 last:border-b-0 last:pb-0"
              >
                <div className="flex flex-col gap-1">
                  <span className="font-medium">{event.name}</span>
                  <div className="text-muted-foreground flex items-center gap-3 text-sm">
                    <span>{dayjs(event.date).format('ddd, MMM D')}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {formatTimeRange(event.startTime, event.endTime)}
                    </span>
                  </div>
                </div>
                <TextLink to={`/accounts/${entityId}/calendar`}>Details</TextLink>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </section>
  )
}

export default UpcomingEventsSection
